import { PageLayout } from '@ipfabric/ui-components/PageLayout/PageLayout';
import { memo, useEffect, useState } from 'react';
import type { FC } from 'react';

import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import {
  type GraphUrlResponseBody,
  createSharedViewGraphLink,
} from '@ipfabric/utils';
import { GraphLoader } from 'components/Graph/Graph';
import { navigateGraphToLocation } from 'components/Graph/helpers/graphLink';
import { useLocale } from 'containers/App/LocaleContext';
import { useLogError } from 'containers/ErrorBoundary/useLogError';
import { useSnapshots } from 'pages/SnapshotManagement/useSnapshots';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useChangeSnapshot } from 'utils/hooks/useChangeSnapshot';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';

const BareShareCanvasGraphPage: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const apiRequest = useApi().request;
  const logError = useLogError();
  const notifications = useNotifications();
  const { viewUrlId } = params;
  const graphMsg = useLocale().canvasGraph;
  const loadedSnapshots = useSnapshots();
  const changeSnapshot = useChangeSnapshot();
  const [error, setError] = useState<'notFound' | 'unknown' | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally triggered only if url id changes
  useEffect(() => {
    if (viewUrlId === undefined) {
      return;
    }
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
    const fetchViewUrlData = async () => {
      try {
        const viewUrlData = await apiRequest<GraphUrlResponseBody>({
          url: `/graphs/urls/${viewUrlId}`,
          method: 'GET',
        });

        if (loadedSnapshots.some(({ id }) => viewUrlData.snapshot === id)) {
          changeSnapshot({ snapshotId: viewUrlData.snapshot });
        }

        navigate(
          ...navigateGraphToLocation(
            createSharedViewGraphLink({
              parameters: {
                viewUrlId,
                viewUrlData,
              },
            }),
          ),
        );
      } catch (e) {
        if (
          isIpfError(e, 'API') &&
          e.payload?.response.code === 'API_NOT_FOUND'
        ) {
          setError('notFound');
        } else {
          setError('unknown');
          logError(e, 'GRAPH');
          notifications.showNotification({
            type: 'danger',
            message: isIpfError(e) ? e.message : graphMsg.view.error.unknown,
          });
        }
      }
    };
    fetchViewUrlData();
  }, [viewUrlId]);

  if (error) {
    const errorMsg = graphMsg.view.error[error];
    return (
      <PageLayout>
        <Helmet title={errorMsg} />
        <h1 className='text-level-3 my-lg'>{graphMsg.view.errorTitle}</h1>
        <p>{errorMsg}</p>
        <Link to='/diagrams/topology-tree'>
          {graphMsg.view.continueToGraphs}
        </Link>
      </PageLayout>
    );
  }

  return <GraphLoader visible message={graphMsg.loadingStates.LOADING ?? ''} />;
};

BareShareCanvasGraphPage.whyDidYouRender = true;

export const ShareCanvasGraphPage = memo(BareShareCanvasGraphPage);
